import React, { useState, useRef, useEffect } from "react";
import styles from "./music.module.css";

const formatTime = (time) => {
  if (!Number.isFinite(time) || time < 0) return "0:00";
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60);
  return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
};

const AudioPlayer = ({ id, audioSrc, title, activeId, setActiveId }) => {
  const isActive = activeId === id;

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.5);

  const audioRef = useRef(null);

  // If another track becomes active, pause this one
  useEffect(() => {
    if (!isActive && isPlaying) {
      audioRef.current?.pause();
      setIsPlaying(false);
    }
  }, [isActive, isPlaying]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onTime = () => setCurrentTime(audio.currentTime || 0);
    const onLoaded = () => setDuration(audio.duration || 0);
    const onEnded = () => setIsPlaying(false);

    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("loadedmetadata", onLoaded);
    audio.addEventListener("ended", onEnded);

    return () => {
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("loadedmetadata", onLoaded);
      audio.removeEventListener("ended", onEnded);
    };
  }, []);

  const togglePlay = async () => {
    const audio = audioRef.current;
    if (!audio) return;

    // Set default volume on mount
    audio.volume = 0.5;

    // claim active track
    setActiveId(id);

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      try {
        await audio.play();
        setIsPlaying(true);
      } catch {
        // autoplay restrictions etc.
        setIsPlaying(false);
      }
    }
  };

  const handleScrub = (event) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    const pct = Number(event.target.value);
    const t = (pct / 100) * duration;
    audio.currentTime = t;
    setCurrentTime(t);
  };

  const handleVolumeChange = (event) => {
    const audio = audioRef.current;
    if (!audio) return;
    const newVol = Number(event.target.value) / 100;
    audio.volume = newVol;
    setVolume(newVol);
  };

  const progressPct = duration ? (currentTime / duration) * 100 : 0;

  return (
    <div className={`${styles.trackCard} ${isActive ? styles.trackCardActive : ""}`}>
      <audio ref={audioRef} src={audioSrc} preload="metadata" />

      <div className={styles.trackHeader}>
        <div className={styles.trackTitle}>{title}</div>

        <button className={styles.playButton} onClick={togglePlay}>
          {isPlaying ? "Pause" : "Play"}
        </button>
      </div>

      <div className={styles.scrubRow}>
        <span className={styles.timeText}>{formatTime(currentTime)}</span>

        <input
          className={styles.scrub}
          type="range"
          min="0"
          max="100"
          value={progressPct || 0}
          onChange={handleScrub}
        />

        <span className={styles.timeText}>{formatTime(duration)}</span>
      </div>

      <div className={styles.volumeRow}>
        <span className={styles.volLabel}>Volume</span>
        <input
          className={styles.volume}
          type="range"
          min="0"
          max="100"
          value={Math.round(volume * 100)}
          onChange={handleVolumeChange}
        />
        <span className={styles.volPct}>{Math.round(volume * 100)}%</span>
      </div>
    </div>
  );
};

const TrackSection = ({ title, tracks, activeId, setActiveId, baseId }) => {
  return (
    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>{title}</h2>
      <div className={styles.trackGrid}>
        {tracks.map((t, i) => (
          <AudioPlayer
            key={baseId + i}
            id={baseId + i}
            audioSrc={t.src}
            title={t.title}
            activeId={activeId}
            setActiveId={setActiveId}
          />
        ))}
      </div>
    </section>
  );
};

export const Music = () => {
  const [activeId, setActiveId] = useState(null);

  const mainTracks = [
    { title: 'Track 01: "Cloudy Sky"', src: "https://www.dropbox.com/scl/fi/1knys1qltyf254wkpr913/track01.mp3?rlkey=fogwvbpf91leviw39kqhfe70l&st=c2dj2a15&dl=1" },
    { title: 'Track 02: "Hurricane Watch"', src: "https://www.dropbox.com/scl/fi/cwnphq80tezuob98vtgmm/Sdslayer100-Track-02.mp3?rlkey=ecimp4k1hpw3gdyrrn0i773n9&st=ft2gr76l&dl=1" },
    { title: 'Track 03: "Countdown to Destruction"', src: "https://www.dropbox.com/scl/fi/9uifcqd51esw2h9rfmryb/Track-03.mp3?rlkey=trr6mcr7vg3ihbqz87nokfxsq&st=jn1sotlp&dl=1" },
    { title: 'Track 04: "Overcast"', src: "https://www.dropbox.com/scl/fi/axx9f78ipme9m7pimjmgi/Track-04-v2022-A.mp3?rlkey=q4bfeuh3d57m9v4rukfsb86ol&st=ut7upxnk&dl=1" },
    { title: 'Track 05: "Storm Surge"', src: "https://www.dropbox.com/scl/fi/1uvt3d5a38iwrh7dmiwsj/Track-05-backup.mp3?rlkey=k6sgr9vqzubiqqyd8ew85q2fx&st=6xfq94t2&dl=1" },
    { title: 'Track 06: "The Day After Landfall"', src: "https://www.dropbox.com/scl/fi/jimhoiwijl64dr6wmtcwd/Track-06.mp3?rlkey=gxk3kn1ilpmuzn3ul0a3zct4y&st=2ujeqitk&dl=1" },
    { title: 'Track 07: "Signal Five"', src: "https://www.dropbox.com/scl/fi/hdz0c50ph53iz6gfyindj/Signal-Five.mp3?rlkey=q1sgn8x8mobu7dxpp5gg4xcqi&st=7xy5f2p2&dl=1" },
    { title: 'Track 08: "Iota"', src: "https://www.dropbox.com/scl/fi/sje658c9l6ci6c4ze226c/spotifydown.com-Iota.mp3?rlkey=551ayjhljmixzpu1hhfpo0kwv&st=ebiahkjw&dl=1" },
    { title: 'Track 09: "Landfall"', src: "https://www.dropbox.com/scl/fi/3i70ikc3sov1bs3wjcp21/Landfall-hurricane-ver.mp3?rlkey=9vq3x2m0796zamfhmd7rqpkl6&st=1jqubkis&dl=1" },
    { title: 'Track 10: "One5Seven"', src: "https://www.dropbox.com/scl/fi/93mxa8b5ni2ir71swmuq7/One5Seven.mp3?rlkey=6nqley82japr1u2xvg2iu0gom&st=ti3v25by&dl=1" },
    { title: 'Track 11: "Show Up!"', src: "https://www.dropbox.com/scl/fi/hi5ywfuhnw5u4cd02x22t/Track-11.mp3?rlkey=8y33f08dt2w1s66ebj1x7halc&st=yc8tzhyj&dl=1" },
    { title: 'Track 12: "11:01"', src: "https://www.dropbox.com/scl/fi/v69syuf6xq4s3mtyx4o5o/Track-12.mp3?rlkey=3pihrcmwbkxllfbozso77h9bt&st=jt7nhb76&dl=1" },
    { title: 'Track 13: "Terminal Four"', src: "https://www.dropbox.com/scl/fi/wtdp99qzdy2ull09wxmiq/Track-13-idk-who-cares.mp3?rlkey=y2qpjn7uuhj0c6jicmb3powaa&st=t2pchh91&dl=1" },
    { title: 'Track 14: "Twelve2One"', src: "https://www.dropbox.com/scl/fi/alciao178rcvyes4xp2ts/Twelve2One.mp3?rlkey=n1hcrjqzkcqa3w9e45srpeaph&st=b1n80tso&dl=1" },
    { title: 'Track 15: "Entry Point"', src: "https://www.dropbox.com/scl/fi/46swiw4j4yfgc1rus5ntc/Track-15.mp3?rlkey=zf1rbwjtyi4n9l5ttnlpm85s9&st=vx9iod0n&dl=1" },
    { title: 'Track 16: "System Check"', src: "https://www.dropbox.com/scl/fi/v7suo5729e6ymaueemt9s/track-16-2.mp3?rlkey=kytxkwy3yyz72nkra3bffcfyd&st=0s1lw67p&dl=1" },
  ];

  const miscTracks = [
    { title: '"Dash All Night"', src: "https://www.dropbox.com/scl/fi/kvuy3vrakq12xucksn02l/dashallnight.mp3?rlkey=y6um8eh6xbfb2uha2f6f2jgy5&st=u53geahe&dl=1" },
    { title: 'Track 10b: "One5Seven Part II"', src: "https://www.dropbox.com/scl/fi/6azknkra9pnws3ga7eodp/One5Seven-Part-II-2024.mp3?rlkey=n7aesj1ouw3hqskrtqqin224k&st=6qs99kxz&dl=1" },
    { title: 'Track 10c: "One5Seven Part III"', src: "https://www.dropbox.com/scl/fi/hdgid87zovrhbibpzwaaz/One5Seven-Part-III-2-1.mp3?rlkey=0osp9617soiyp2r3qm3hduxxp&st=54cn57qz&dl=1" },
    { title: 'Track 02 v2024: "Hurricane Watch v2024"', src: "https://www.dropbox.com/scl/fi/o70ymmvqyozendf84ms4l/Track-02-rm-2.mp3?rlkey=bpu9fez0ueq0vqnt9mkmuj29z&st=g144wuse&dl=1" },
  ];

  return (
    <div className={styles.homePage}>
      <div className={styles.shell}>
        <header className={styles.header}>
          <h1 className={styles.title}>I make music sometimes</h1>
        </header>

        <TrackSection
          title="Main Tracks"
          tracks={mainTracks}
          activeId={activeId}
          setActiveId={setActiveId}
          baseId={1000}
        />

        <TrackSection
          title="Misc. Tracks"
          tracks={miscTracks}
          activeId={activeId}
          setActiveId={setActiveId}
          baseId={2000}
        />

        <a href="../" className={styles.homeLink}>Home Page</a>
      </div>
    </div>
  );
};